// src/utils/checkoutPdf.js
//
// Generates a KA-branded PDF of a student's checkout record.
//
// Requires:
//   npm install jspdf jspdf-autotable
//
// Usage:
//   import { generateCheckoutPdf } from '../utils/checkoutPdf';
//   await generateCheckoutPdf(record);
//
// The function downloads the file directly via jsPDF's `save()`. Filename
// format: "Checkout_<LastName>_<FirstName>_<schoolYear>.pdf"

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  CHECKOUT_SECTIONS,
  CHECKOUT_FORMS,
  DORM_INVENTORY_ITEMS,
  SECTION_STATUS,
  KA_COLORS,
  getRelevantSectionIds,
  formatSignoffTimestamp,
  computeCompletionPercent,
} from './checkoutHelpers';

// -----------------------------------------------------------------------------
// Color helpers — autotable wants RGB tuples, not hex.
// -----------------------------------------------------------------------------

const hexToRgb = (hex) => {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
};

/**
 * Returns the active color palette based on the colorMode option:
 *   - 'color'     (default) — KA brand palette
 *   - 'grayscale' — all colors mapped to grays for B&W printing.
 *
 * In grayscale, status pills are visually distinguished by:
 *   - Signed:   light gray fill + bold black text + ✓ prefix
 *   - Pending:  medium gray fill + bold black text
 *   - N/A:      em-dash in plain text (no pill fill)
 * This keeps the printed output readable on a black-only laser printer
 * without burning color toner.
 */
function buildPalette(colorMode) {
  if (colorMode === 'grayscale') {
    return {
      red: [60, 60, 60],
      navy: [30, 30, 30],
      gold: [120, 120, 120],
      white: [255, 255, 255],
      lightGray: [245, 245, 245],
      borderGray: [200, 200, 200],
      textGray: [80, 80, 80],
      // Status pill mappings
      signedFill: [220, 220, 220],
      signedText: [0, 0, 0],
      pendingFill: [180, 180, 180],
      pendingText: [0, 0, 0],
      naFill: [255, 255, 255],
      naText: [120, 120, 120],
      // Progress bar: just black-on-light
      progressFill: [60, 60, 60],
      progressBg: [220, 220, 220],
      progressComplete: [0, 0, 0],
    };
  }
  // Default: full color
  return {
    red: hexToRgb(KA_COLORS.red),
    navy: hexToRgb(KA_COLORS.navy),
    gold: hexToRgb(KA_COLORS.gold),
    white: [255, 255, 255],
    lightGray: hexToRgb(KA_COLORS.lightGray),
    borderGray: [224, 224, 224],
    textGray: [85, 85, 85],
    // Status pills: green / yellow / gray
    signedFill: [212, 237, 218],
    signedText: [21, 87, 36],
    pendingFill: [255, 243, 205],
    pendingText: [133, 100, 4],
    naFill: [233, 236, 239],
    naText: [108, 117, 125],
    progressFill: hexToRgb(KA_COLORS.gold),
    progressBg: [224, 224, 224],
    progressComplete: [21, 87, 36],
  };
}

// -----------------------------------------------------------------------------
// Section status formatting for the table cells
// -----------------------------------------------------------------------------

// Module-level active palette. The `generateCheckoutPdf` and
// `buildCheckoutPdfBlob` entry points reset this based on the colorMode
// option before any drawing happens. Default is full color so callers
// that don't pass options behave as they always have.
let P = buildPalette('color');
let CURRENT_COLOR_MODE = 'color';

const formatStatusCell = (sectionData) => {
  const isBw = CURRENT_COLOR_MODE === 'grayscale';
  if (!sectionData) {
    return {
      text: 'Pending',
      fill: P.pendingFill,
      textColor: P.pendingText,
    };
  }
  switch (sectionData.status) {
    case SECTION_STATUS.SIGNED:
      return {
        text: isBw ? '✓ Signed' : 'Signed',
        fill: P.signedFill,
        textColor: P.signedText,
      };
    case SECTION_STATUS.NA:
      return {
        text: isBw ? '—' : 'N/A',
        fill: P.naFill,
        textColor: P.naText,
      };
    case SECTION_STATUS.PENDING:
    default:
      return {
        text: 'Pending',
        fill: P.pendingFill,
        textColor: P.pendingText,
      };
  }
};

// -----------------------------------------------------------------------------
// Header / footer drawing
// -----------------------------------------------------------------------------

const drawHeader = (doc, record) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;

  // Red bar across the top
  doc.setFillColor(...P.red);
  doc.rect(0, 0, pageWidth, 8, 'F');

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...P.navy);
  doc.text("King's Academy", margin, 22);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...P.textGray);
  doc.text('Madaba, Jordan', margin, 28);

  // Form-type pill (right-aligned)
  const form = CHECKOUT_FORMS.find((f) => f.id === record.formType);
  const formLabel = form ? form.label : record.formType;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...P.white);
  const pillText = formLabel;
  const textWidth = doc.getTextWidth(pillText);
  const pillW = textWidth + 12;
  const pillH = 8;
  const pillX = pageWidth - margin - pillW;
  const pillY = 18;
  doc.setFillColor(...P.navy);
  doc.roundedRect(pillX, pillY, pillW, pillH, 2, 2, 'F');
  doc.text(pillText, pillX + 6, pillY + 5.5);

  // Section title under the header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...P.navy);
  doc.text('Student Exit Checklist', margin, 40);

  // Gold underline
  doc.setDrawColor(...P.gold);
  doc.setLineWidth(0.6);
  doc.line(margin, 42, pageWidth - margin, 42);
};

const drawFooter = (doc, record) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const pageCount = doc.internal.getNumberOfPages();

  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...P.textGray);

    const generatedAt = new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });

    doc.text(
      `Generated ${generatedAt} • School Year ${record.schoolYear || ''}`,
      margin,
      pageHeight - 8,
    );
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth - margin,
      pageHeight - 8,
      { align: 'right' },
    );
  }
};

// -----------------------------------------------------------------------------
// Student info block
// -----------------------------------------------------------------------------

const drawStudentInfo = (doc, record, startY) => {
  const margin = 14;
  const pageWidth = doc.internal.pageSize.getWidth();
  const completion = computeCompletionPercent(record);

  // Background panel
  doc.setFillColor(...P.lightGray);
  doc.rect(margin, startY, pageWidth - margin * 2, 28, 'F');

  // Left column: name, grade
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...P.navy);
  doc.text(record.studentName || '—', margin + 4, startY + 8);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...P.textGray);

  const leftParts = [];
  if (record.grade) leftParts.push(record.grade);
  if (record.cohort) leftParts.push(record.cohort);
  if (record.studentId) leftParts.push(`SIS ${record.studentId}`);
  doc.text(leftParts.join('  •  '), margin + 4, startY + 14);

  if (record.cohort === 'Boarding') {
    const dormLine = [];
    if (record.dorm) dormLine.push(`Dorm: ${record.dorm}`);
    if (record.room) dormLine.push(`Room: ${record.room}`);
    if (dormLine.length > 0) {
      doc.text(dormLine.join('  •  '), margin + 4, startY + 20);
    }
  }

  // Right column: completion %
  const pctText = `${completion}% Complete`;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...P.navy);
  doc.text(pctText, pageWidth - margin - 4, startY + 12, { align: 'right' });

  // Progress bar
  const barX = pageWidth - margin - 64;
  const barY = startY + 16;
  const barW = 60;
  const barH = 4;
  doc.setFillColor(...P.progressBg);
  doc.roundedRect(barX, barY, barW, barH, 1, 1, 'F');
  if (completion > 0) {
    const fillColor = completion === 100 ? P.progressComplete : P.progressFill;
    doc.setFillColor(...fillColor);
    doc.roundedRect(barX, barY, (barW * completion) / 100, barH, 1, 1, 'F');
  }

  return startY + 28 + 6; // y position after the panel + spacing
};

// -----------------------------------------------------------------------------
// Main signoff table
// -----------------------------------------------------------------------------

const drawSignoffTable = (doc, record, startY) => {
  const relevantIds = getRelevantSectionIds(record);
  // Show ALL sections in the catalog, but mark non-relevant ones distinctly.
  // Per Kyle's request, mirror the paper form — but only the relevant ones
  // really matter, so we show only relevant ones to keep the PDF focused.
  const sectionsToShow = CHECKOUT_SECTIONS.filter((s) =>
    relevantIds.includes(s.id),
  );

  // Build the table body. For sections with a teachers[] array, we emit
  // ONE row per teacher (so each gets its own status/signed-by line). The
  // first row carries the section label and shared description; subsequent
  // rows show just the teacher name and their individual signoff.
  const body = [];
  sectionsToShow.forEach((section) => {
    const data = record.sections?.[section.id] || {};
    const hasTeachers =
      Array.isArray(data.teachers) && data.teachers.length > 0;

    if (hasTeachers) {
      data.teachers.forEach((t, idx) => {
        const teacherDescLines = [];
        if (idx === 0) {
          if (section.description) teacherDescLines.push(section.description);
          if (data.context) teacherDescLines.push(`Notes: ${data.context}`);
        }
        teacherDescLines.push(`Teacher: ${t.name || '(unnamed)'}`);

        // Per-teacher status cell — uses the active palette so multi-teacher
        // sections render correctly in both color and grayscale.
        const isBw = CURRENT_COLOR_MODE === 'grayscale';
        let teacherStatus;
        if (t.signedAt) {
          teacherStatus = {
            text: isBw ? '✓ SIGNED' : 'SIGNED',
            fill: P.signedFill,
            textColor: P.signedText,
          };
        } else {
          teacherStatus = {
            text: 'PENDING',
            fill: P.pendingFill,
            textColor: P.pendingText,
          };
        }

        let signedByCell = '';
        if (t.signedAt) {
          signedByCell = t.signedByName || '';
          signedByCell += `\n${formatSignoffTimestamp(t.signedAt)}`;
        }

        body.push([
          idx === 0 ? section.label : '',
          teacherDescLines.join('\n'),
          {
            content: teacherStatus.text,
            styles: {
              fillColor: teacherStatus.fill,
              textColor: teacherStatus.textColor,
              fontStyle: 'bold',
              halign: 'center',
            },
          },
          signedByCell,
          t.notes || '',
        ]);
      });
      return;
    }

    // Single-signer (legacy) section row
    const status = formatStatusCell(data);
    let signedBy = '';
    if (data.status === SECTION_STATUS.SIGNED) {
      signedBy = data.signedByName || '';
      if (data.signedAt) {
        signedBy += `\n${formatSignoffTimestamp(data.signedAt)}`;
      }
    } else if (data.status === SECTION_STATUS.NA) {
      signedBy = '—';
    }

    const descLines = [];
    if (section.description) descLines.push(section.description);
    if (data.teacherName) descLines.push(`Teacher: ${data.teacherName}`);
    if (data.context) descLines.push(`Notes: ${data.context}`);

    body.push([
      section.label,
      descLines.join('\n'),
      {
        content: status.text,
        styles: {
          fillColor: status.fill,
          textColor: status.textColor,
          fontStyle: 'bold',
          halign: 'center',
        },
      },
      signedBy,
      data.notes || '',
    ]);
  });

  autoTable(doc, {
    startY,
    head: [['Department', 'Checking for', 'Status', 'Signed by', 'Notes']],
    body,
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: 9,
      cellPadding: 2.5,
      textColor: [40, 40, 40],
      lineColor: [220, 220, 220],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: P.navy,
      textColor: P.white,
      fontStyle: 'bold',
      fontSize: 9,
    },
    columnStyles: {
      0: { cellWidth: 38, fontStyle: 'bold', textColor: P.navy },
      1: { cellWidth: 60 },
      2: { cellWidth: 22, halign: 'center' },
      3: { cellWidth: 32 },
      4: { cellWidth: 'auto' },
    },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    margin: { left: 14, right: 14 },
  });

  return doc.lastAutoTable.finalY + 6;
};

// -----------------------------------------------------------------------------
// Dorm inventory section (only printed if the record's dorm section is relevant)
// -----------------------------------------------------------------------------

const drawDormInventory = (doc, record, startY) => {
  const dorm = record.sections?.dorm;
  if (!dorm || dorm.status === SECTION_STATUS.NA) return startY;

  const pageHeight = doc.internal.pageSize.getHeight();
  if (startY > pageHeight - 80) {
    doc.addPage();
    startY = 20;
  }

  const margin = 14;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...P.navy);
  doc.text('Dorm Room Inventory', margin, startY);

  doc.setDrawColor(...P.gold);
  doc.setLineWidth(0.4);
  doc.line(margin, startY + 1.5, margin + 50, startY + 1.5);

  startY += 6;

  // Two-column layout for the 15 items
  const inventory = dorm.inventory || {};
  const half = Math.ceil(DORM_INVENTORY_ITEMS.length / 2);
  const leftCol = DORM_INVENTORY_ITEMS.slice(0, half);
  const rightCol = DORM_INVENTORY_ITEMS.slice(half);

  const colWidth = (doc.internal.pageSize.getWidth() - margin * 2) / 2;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(40, 40, 40);

  const drawCol = (items, colX, colY) => {
    let y = colY;
    items.forEach((item) => {
      const checked = !!inventory[item.id];
      // Box
      doc.setDrawColor(120, 120, 120);
      doc.setLineWidth(0.3);
      doc.rect(colX, y - 3.2, 3.5, 3.5);
      if (checked) {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...P.signedText);
        doc.text('✓', colX + 0.4, y);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(40, 40, 40);
      }
      doc.text(item.label, colX + 6, y);
      y += 5.5;
    });
    return y;
  };

  const yLeftEnd = drawCol(leftCol, margin, startY);
  const yRightEnd = drawCol(rightCol, margin + colWidth, startY);
  const yEnd = Math.max(yLeftEnd, yRightEnd);

  // Damage notes
  if (dorm.damageNotes && dorm.damageNotes.trim().length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...P.navy);
    doc.text('Damage Notes:', margin, yEnd + 4);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(40, 40, 40);
    const wrappedNotes = doc.splitTextToSize(
      dorm.damageNotes,
      doc.internal.pageSize.getWidth() - margin * 2,
    );
    doc.text(wrappedNotes, margin, yEnd + 9);

    return yEnd + 9 + wrappedNotes.length * 4 + 4;
  }

  return yEnd + 4;
};

// -----------------------------------------------------------------------------
// Form assignment / audit info
// -----------------------------------------------------------------------------

const drawAuditFooter = (doc, record, startY) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (startY > pageHeight - 30) {
    doc.addPage();
    startY = 20;
  }

  const margin = 14;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(...P.textGray);

  const lines = [];
  if (record.formAssignedByName && record.formAssignedAt) {
    lines.push(
      `Form assigned by ${record.formAssignedByName} on ${formatSignoffTimestamp(record.formAssignedAt)}`,
    );
  }
  if (Array.isArray(record.formHistory) && record.formHistory.length > 0) {
    record.formHistory.forEach((entry) => {
      lines.push(
        `Form changed from "${entry.from}" to "${entry.to}" by ${entry.changedByName || 'unknown'} on ${formatSignoffTimestamp(entry.changedAt)}${entry.reason ? ` — ${entry.reason}` : ''}`,
      );
    });
  }

  if (lines.length === 0) return startY;

  let y = startY + 4;
  lines.forEach((line) => {
    const wrapped = doc.splitTextToSize(line, doc.internal.pageSize.getWidth() - margin * 2);
    doc.text(wrapped, margin, y);
    y += wrapped.length * 3.5 + 1;
  });

  return y;
};

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

/**
 * Generate and download a PDF of a student's checkout record.
 *
 * @param {object} record - the full checkout record document
 */
/**
 * Internal: render a single record into the given jsPDF instance,
 * starting at the current page. Returns the page index range used.
 *
 * Options:
 *   colorMode: 'color' | 'grayscale' (default 'color')
 *   duplex:    boolean — pad to even page count for clean duplex printing
 *              (default false)
 *
 * If `doc` is omitted, a new document is created. If passed, the caller is
 * responsible for `addPage()`-ing before calling this so the new record
 * starts on a fresh page (used by the batch generator).
 */
function renderRecordIntoDoc(doc, record, options = {}) {
  const colorMode = options.colorMode === 'grayscale' ? 'grayscale' : 'color';
  const duplex = !!options.duplex;

  // Snapshot current page count BEFORE rendering, so we can compute the
  // record's own page span and pad to even within that span.
  const startingPageCount = doc.internal.getNumberOfPages();

  // Activate palette for this render. (Module-level state — fine because
  // PDF rendering is synchronous.)
  P = buildPalette(colorMode);
  CURRENT_COLOR_MODE = colorMode;

  drawHeader(doc, record);
  let y = drawStudentInfo(doc, record, 48);
  y = drawSignoffTable(doc, record, y);
  y = drawDormInventory(doc, record, y);
  drawAuditFooter(doc, record, y);

  // After rendering, autotable may have added pages. Compute how many
  // pages this record actually occupies.
  const finalPageCount = doc.internal.getNumberOfPages();
  let recordPageCount = finalPageCount - startingPageCount + 1;

  if (duplex && recordPageCount % 2 === 1) {
    // Pad with a blank "intentionally left blank" page so the next
    // record starts on a fresh sheet when duplex-printed.
    doc.addPage();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(170, 170, 170);
    doc.text(
      'This page intentionally left blank — duplex spacer.',
      pageWidth / 2,
      pageHeight / 2,
      { align: 'center' },
    );
    recordPageCount += 1;
  }

  return {
    firstPage: startingPageCount,
    lastPage: doc.internal.getNumberOfPages(),
    pages: recordPageCount,
  };
}

/**
 * Generate a single-student checkout PDF and download it.
 *
 * Options:
 *   colorMode: 'color' | 'grayscale' (default 'color')
 *   duplex:    boolean — pad to 2 pages for clean duplex printing
 *              (default false; individual PDFs typically don't need it)
 */
export const generateCheckoutPdf = (record, options = {}) => {
  if (!record) {
    throw new Error('generateCheckoutPdf: record is required');
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  renderRecordIntoDoc(doc, record, options);
  drawFooter(doc, record);

  // Filename
  const safeLast = (record.studentName || 'Student')
    .split(' ')
    .slice(-1)[0]
    .replace(/[^a-z0-9]/gi, '');
  const safeFirst = (record.studentName || 'Student')
    .split(' ')[0]
    .replace(/[^a-z0-9]/gi, '');
  const yr = record.schoolYear || 'Unknown';
  const filename = `Checkout_${safeLast}_${safeFirst}_${yr}.pdf`;

  doc.save(filename);
};

/**
 * Same as generateCheckoutPdf, but returns the PDF as a Blob instead of
 * downloading. Useful for previews, attaching to email, etc.
 */
export const buildCheckoutPdfBlob = (record, options = {}) => {
  if (!record) {
    throw new Error('buildCheckoutPdfBlob: record is required');
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  renderRecordIntoDoc(doc, record, options);
  drawFooter(doc, record);

  return doc.output('blob');
};

/**
 * Batch-generate one big PDF containing every record's checkout sheet,
 * back-to-back, in alphabetical order by studentName.
 *
 * Options:
 *   colorMode:    'color' | 'grayscale' (default 'grayscale' for batch —
 *                 saves color toner for year-end printing)
 *   duplex:       boolean — pad each record to even page count so duplex
 *                 printing keeps records on separate sheets (default true)
 *   filename:     output filename (default: Checkouts_<year>_<date>.pdf)
 *   onProgress:   callback(currentIndex, totalCount, recordName) for UI
 *                 progress feedback
 *   download:     boolean — auto-download when done (default true). When
 *                 false, returns the Blob instead.
 *
 * Returns: undefined when download=true, or { blob, pageCount } when
 * download=false.
 */
export const generateBatchCheckoutPdf = async (records, options = {}) => {
  if (!Array.isArray(records) || records.length === 0) {
    throw new Error('generateBatchCheckoutPdf: at least one record is required');
  }

  const colorMode = options.colorMode === 'color' ? 'color' : 'grayscale';
  const duplex = options.duplex !== false; // default true
  const onProgress = typeof options.onProgress === 'function' ? options.onProgress : null;
  const shouldDownload = options.download !== false; // default true

  // Sort alphabetically by studentName. Names typically arrive as
  // "Last, First" so a plain locale-aware compare is enough.
  const sorted = [...records].sort((a, b) =>
    (a.studentName || '').localeCompare(b.studentName || ''),
  );

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  for (let i = 0; i < sorted.length; i += 1) {
    const record = sorted[i];
    if (i > 0) doc.addPage();
    renderRecordIntoDoc(doc, record, { colorMode, duplex });

    if (onProgress) {
      // eslint-disable-next-line no-await-in-loop
      await Promise.resolve(); // yield so the UI can paint
      onProgress(i + 1, sorted.length, record.studentName || record.studentId);
    }
  }

  // Footer applies to every page (page numbers, generation timestamp).
  // We use the first record's schoolYear since they should all be the same.
  drawFooter(doc, sorted[0]);

  const today = new Date().toISOString().slice(0, 10);
  const yr = sorted[0].schoolYear || 'Unknown';
  const filename =
    options.filename || `Checkouts_${yr}_${today}.pdf`;

  if (shouldDownload) {
    doc.save(filename);
    return undefined;
  }
  return {
    blob: doc.output('blob'),
    filename,
    pageCount: doc.internal.getNumberOfPages(),
  };
};